/**
 * GS1 mod-10 check digit, length-independent. Processes `body` right-to-left
 * with alternating weights 3,1,3,1… (rightmost digit ×3), which is the
 * canonical GS1 algorithm shared by GTIN-12/13/14 and SSCC-18 — only the
 * length differs. Returns the single check-digit character.
 *
 * Throws on any non-digit input; callers that may hold partial/typed input
 * should guard on `/^\d+$/` first.
 */
export function computeGtinCheckDigit(body: string): string {
  if (!/^\d+$/.test(body)) {
    throw new Error(
      `computeGtinCheckDigit: expected ASCII digits, got ${JSON.stringify(body)}`
    )
  }
  let sum = 0
  for (let j = 0; j < body.length; j++) {
    const digit = body.charCodeAt(body.length - 1 - j) - 48
    const weight = j % 2 === 0 ? 3 : 1
    sum += digit * weight
  }
  return String((10 - (sum % 10)) % 10)
}

export type CheckDigitValidation = { ok: true } | { ok: false; reason: string }

/**
 * Validates that the LAST digit of `value` is the correct GS1 check digit over
 * the preceding digits. `value` must be all digits and length ≥ 2.
 */
export function validateGtinCheckDigit(value: string): CheckDigitValidation {
  if (!/^\d{2,}$/.test(value)) {
    return { ok: false, reason: 'value must be ≥2 ASCII digits' }
  }
  const body = value.slice(0, -1)
  const expected = computeGtinCheckDigit(body)
  const actual = value.slice(-1)
  return expected === actual
    ? { ok: true }
    : { ok: false, reason: `check digit mismatch: expected ${expected}, got ${actual}` }
}
